"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import { PageCompetitionAffiliateSchema } from "@/sanity/pages/PageCompetitionAffiliateTemplate";
import { CTAButton } from "@/shadcn/ui/button";
import { PageAffiliateWorkshopSchema } from "@/sanity/pages/PageAffiliateWorkshopTemplate";

export default function DealSection({
  data,
  className,
}: {
  data: PageAffiliateWorkshopSchema;
  className?: string;
}) {
  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
              index == 3 && "lg:h-[50%] lg:self-end",
            )}
          />
        ))}
      </div>

      <div className="container flex flex-col items-center pt-[8rem] lg:pt-[15.25rem] pb-[7rem] lg:pb-[20rem] relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <ChipSerif text={data.dealChip} />
        </motion.div>

        {data?.dealTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext w-[min(90rem,85%)]"
          >
            <PortableText
              value={data.dealTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        <div className="mt-[2.5rem] lg:mt-[4.75rem] w-[min(175rem,100%)] grid grid-cols-1 lg:grid-cols-3 gap-[2.75rem] lg:gap-[5.13rem]">
          {data.dealList &&
            data.dealList.length > 0 &&
            data.dealList.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: 0.2 * index,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                key={"deal" + index}
                className="bg-white border border-[#E5E5E5] p-[2.25rem] pb-[4rem] flex flex-col gap-[3rem]"
              >
                {item.image && (
                  <Image
                    src={item.image}
                    alt=""
                    className="!w-full rounded-[1.5rem] lg:rounded-[1.75rem]"
                  />
                )}
                <span className="text-[#103037] font-geist text-[3rem] font-medium tracking-[-0.09rem] capitalize leading-[120%]">
                  {item.title}
                </span>

                {item.bullets &&
                  item.bullets.length > 0 &&
                  item.bullets.map((bullet, indexb) => (
                    <div
                      key={"deal" + index + "bullet" + indexb}
                      className="flex items-center gap-[1.5rem]"
                    >
                      <div className="bg-[#103037] shrink-0 w-[0.875rem] h-[0.875rem]" />
                      <div className="richtext richtext-start prose-p:text-[2rem]">
                        <PortableText
                          value={bullet.text}
                          components={richTextComponent}
                        />
                      </div>
                    </div>
                  ))}
              </motion.div>
            ))}
        </div>

        <div className="w-full lg:w-fit mt-[4.25rem]">
          <CTAButton data={data.dealCTA} />
        </div>
      </div>
    </section>
  );
}
